import { Paper } from '@mantine/core';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { Map, MapEvent } from 'mapbox-gl';
import { useCallback, useEffect, useRef } from 'react';
import { MapRef } from 'react-map-gl';

import { useQueriesWithGlobal } from '@data-access';
import {
  asFieldsFeatureCollectionFromFeatures,
  fieldsQueries,
  hasFieldGeometry,
} from '@fields/data-access';
import { hasZoomableGeometry, PfMap, PlotsLayer, zoomOnFeature } from '@map';

export function FieldMap() {
  const { fieldId } = useParams({ from: '/_app/fields_/$fieldId' });
  const mapRef = useRef<MapRef>(null);

  const queries = useQueriesWithGlobal(fieldsQueries);
  const { data } = useSuspenseQuery(queries.detail(Number(fieldId)));
  const maxBounds = useQuery(queries.maxBounds());

  const fitMapView = useCallback(
    (map?: Map) => {
      if (!map) {
        return;
      }

      if (hasZoomableGeometry(data)) {
        zoomOnFeature({
          map,
          geojson: data,
        });
        return;
      }

      if (maxBounds.data?.bbox) {
        map.fitBounds(maxBounds.data.bbox, { padding: 20 });
      }
    },
    [data, maxBounds.data],
  );

  const handleLoad = (map: MapEvent) => {
    fitMapView(map.target);
  };

  useEffect(() => {
    if (maxBounds.isSuccess) {
      fitMapView(mapRef.current?.getMap());
    }
  }, [fitMapView, maxBounds.isSuccess]);

  return (
    <Paper h="100%" p={0}>
      <PfMap onLoad={handleLoad} ref={mapRef}>
        {hasFieldGeometry(data) ? (
          <PlotsLayer
            data={asFieldsFeatureCollectionFromFeatures([data])}
          />
        ) : null}
      </PfMap>
    </Paper>
  );
}

export default FieldMap;
